<!-- 
Listing of invoices.

Can be filtered to show:
- all invoices
- invoices for a single levy collection
- invoices for a single provider
- invoices for a levy collection provider
-->
<template>
  <div>
    <v-card :class="embedded ? 'v-card--flat' : 'home'">
      <our-table-header 
        :listing="listing"
        :search-filters="searchFilters"
        :criteria-mutation="criteriaMutation"
        :has-export="hasExport"
        @update:criteria="refresh"
        @update:pageNumber="updatePageNumber" 
        @click:refresh="refresh" 
        @click:export="exportToExcel" 
      />
      <our-table-busy-indicator :show="listing.loading"/>
      <v-data-table 
        :headers="headers" 
        :items="listing.data" 
        :pagination="listing.paging" 
        :total-items="listing.paging.totalItems" 
        :rows-per-page-items="rowsPerPageItems" 
        :no-data-text="noDataText"
        must-sort 
        hide-actions
        @update:pagination="updatePagination"
      >
        <template slot="items" slot-scope="props">
          <tr @click.stop="rowSelected(props.item.id, props.item)">
            <our-nav-col :row="props.item.date"/>
            <td class="pa-0 ma-0 text-xs-center">
              <our-warnings-tooltip :warnings="props.item.warnings" />
            </td>
            <td>{{ lookups.invoiceStatusDescriptions[props.item.status] }}</td>
            <td>{{ getPaymentStatusDescription(props.item) }}</td>
            <td>{{ props.item.due | compactDate }}</td>
            <td class="text-xs-center">{{ getDaysOverdue(props.item) }}</td>
            <td>{{ props.item.sapDocumentIdentifier }}</td>
            <td>{{ props.item.amount | currency }}</td>
            <td v-if="levyCollectionId == null">{{ props.item.levyCollection }}</td>
            <td v-if="providerId == null">{{ props.item.provider }}</td>
            <td v-if="showLevyCollectionProviderShortcut" class="text-xs-center px-2">
              <v-tooltip v-if="!props.item.missing" bottom>
                <v-btn 
                  slot="activator" 
                  :to="`/levy-collections/${props.item.levyCollectionId}/providers/${props.item.providerId}`" 
                  icon small class="ma-0 primary--text"
                >
                  <v-icon small>school</v-icon>
                </v-btn>
                <span>Click to open the levy collection provider</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import tableMixins from "@/mixins/table.mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as action from "@/store/invoices/action-types";
import * as mutation from "@/store/invoices/mutation-types";
import { INVOICES_EXPORT } from "@/store/claim-types";
import { getSearchFilters } from "@/store/invoices/helpers";

export default {
  mixins: [tableMixins, filterMixins, lookupMixins],
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    },
    providerId: {
      type: Number,
      default: null
    },
    defaultNoDataText: {
      type: String,
      default: "No invoices yet."
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      clearCriteriaMutation: mutation.LIST_CLEAR_CRITERIA,
      pagingMutation: mutation.LIST_SET_PAGING
    };
  },
  computed: {
    headers() {
      let headers = [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "", value: "Warnings", sortable: false, width: 10 },
        { text: "Status", value: "Status", sortable: this.canSort, width: 120 },
        { text: "Payment status", value: "AssignedPaymentStatus", sortable: false },
        { text: "Due", value: "Due", sortable: this.canSort, width: 120 },
        { text: "Overdue", value: "Overdue", sortable: false, width: 10 },
        { text: "SAP document ID", value: "SapDocumentIdentifier", sortable: this.canSort },
        { text: "Amount", value: "Amount", sortable: this.canSort }
      ];
      if (this.levyCollectionId == null) {
        headers.push({ text: "Levy collection", value: "LevyCollectionId", sortable: true });
      }
      if (this.providerId == null) {
        headers.push({ text: "Provider", value: "Provider.Name", sortable: true });
      }
      if (this.showLevyCollectionProviderShortcut) {
        headers.push({ text: "", value: "Id", sortable: false, align: "right", width: 100 });
      }
      return headers;
    },
    listing() {
      return this.$store.state.invoices.listing;
    },
    loaded() {
      return this.listing.loaded;
    },
    showLevyCollectionProviderShortcut() {
      return !(!!this.levyCollectionId && !!this.providerId);
    },
    hasExport() {
      return this.$store.getters.claims.includes(INVOICES_EXPORT);
    },
    criteria() {
      return this.listing.criteria;
    },
    searchFilters() {
      return getSearchFilters();
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const hasChanged =
        (!this.listing.loaded && !this.listing.loading) ||
        this.listing.levyCollectionId != this.levyCollectionId ||
        this.listing.providerId != this.providerId;
      if (hasChanged) {
        this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
        this.$store.commit(mutation.LIST_SET_FILTERS, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
        this.populate();
      }
    },
    getDaysOverdue(invoice) {
      if (invoice.status != "Open") return null;
      const today = moment().startOf("day");
      const due = moment(invoice.due);
      if (today.isSameOrBefore(due)) return null;
      return this.pluralise(today.diff(due, "days"));
    },
    getPaymentStatusDescription(invoice) {
      if (invoice.status == "Reversed") return null;
      const status = invoice.paymentStatus;
      const description = this.lookups.paymentStatusDescriptions[status];
      return status == "PaidLate" ? `${description} (${this.pluralise(invoice.providerLatePaymentDays)})` : description;
    },
    pluralise(days) {
      return days == 1 ? "1 day" : `${days} days`;
    }
  }
};
</script>

<style lang="scss">
.late {
  color: red;
}
</style>